import { formatSessionWallTime } from '@lighthouse/shared';
import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface Payout {
  id: string;
  bookingId: string;
  therapistName: string;
  amountNgn: number;
  commissionNgn: number;
  therapistAmountNgn: number;
  sessionDate: string;
  status: 'pending' | 'released' | 'disputed' | 'flagged';
  releasedAt?: string;
  flagReason?: string;
}

interface Summary {
  totalGmv: number;
  totalCommission: number;
  totalPaid: number;
  pendingCount: number;
  flaggedCount: number;
}

interface ReconciliationItem {
  id: string;
  paystackReference: string;
  reason: string;
  status: string;
  bookingId: string | null;
  attempts: number;
  createdAt: string;
}

interface ReconciliationSummary {
  open: number;
  resolvedToday: number;
}

function badge(s: string) {
  const map: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    released: 'bg-green-100 text-green-800',
    disputed: 'bg-red-100 text-red-800',
    flagged: 'bg-red-100 text-red-800',
  };
  return `inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[s] || 'bg-gray-100 text-gray-700'}`;
}

export function PayoutsPage() {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [reconciliation, setReconciliation] = useState<ReconciliationItem[]>([]);
  const [reconSummary, setReconSummary] = useState<ReconciliationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [approving, setApproving] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : '';
    Promise.all([
      adminApi<Payout[]>(`/admin/payouts${q}`),
      adminApi<Summary>('/admin/payouts/summary'),
      adminApi<{ items?: ReconciliationItem[]; summary?: ReconciliationSummary }>(
        '/admin/reconciliation'
      ),
    ])
      .then(([p, s, recon]) => {
        setPayouts(ensureArray(p));
        setSummary(s && typeof s === 'object' && 'totalGmv' in s ? s : null);
        setReconciliation(ensureArray(recon?.items));
        setReconSummary(recon?.summary ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filter]);

  const approve = async (id: string) => {
    setApproving(id);
    try {
      await adminApi(`/admin/payouts/${id}/release`, { method: 'POST' });
      load();
    } finally {
      setApproving(null);
    }
  };

  const resolveReconciliation = async (id: string) => {
    await adminApi(`/admin/reconciliation/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ notes: 'Resolved via admin panel' }),
    });
    load();
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payouts & Escrow</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Manage therapist payouts and reconciliation
          </p>
        </div>
      </div>

      {summary && (
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-5">
          {[
            {
              label: 'Total GMV',
              value: `₦${summary.totalGmv.toLocaleString()}`,
              color: 'text-gray-900',
            },
            {
              label: 'Platform Commission',
              value: `₦${summary.totalCommission.toLocaleString()}`,
              color: 'text-primary-600',
            },
            {
              label: 'Paid to Therapists',
              value: `₦${summary.totalPaid.toLocaleString()}`,
              color: 'text-green-600',
            },
            { label: 'Pending Release', value: summary.pendingCount, color: 'text-yellow-600' },
            { label: 'Flagged', value: summary.flaggedCount, color: 'text-red-600' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-gray-500">{s.label}</p>
              <p className={`mt-1 text-xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {reconSummary && reconSummary.open > 0 && (
        <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h2 className="text-lg font-semibold text-amber-900">
            Payment Reconciliation ({reconSummary.open} open)
          </h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-amber-800">
                  <th className="pb-2 pr-4">Reference</th>
                  <th className="pb-2 pr-4">Reason</th>
                  <th className="pb-2 pr-4">Attempts</th>
                  <th className="pb-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {reconciliation.map((item) => (
                  <tr key={item.id} className="border-t border-amber-100">
                    <td className="py-2 pr-4 font-mono text-xs">{item.paystackReference}</td>
                    <td className="py-2 pr-4">{item.reason}</td>
                    <td className="py-2 pr-4">{item.attempts}</td>
                    <td className="py-2">
                      <button
                        onClick={() => void resolveReconciliation(item.id)}
                        className="text-xs text-amber-900 underline"
                      >
                        Mark resolved
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-gray-100 p-4">
          {['', 'pending', 'released', 'disputed', 'flagged'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full border px-3 py-1 text-sm transition-colors ${filter === s ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'}`}
            >
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading payouts…</div>
        ) : payouts.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No payouts found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-3">Therapist</th>
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">Gross</th>
                  <th className="px-4 py-3">Commission</th>
                  <th className="px-4 py-3">Net Payout</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payouts.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{p.therapistName}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatSessionWallTime(
                        p.sessionDate,
                        { day: 'numeric', month: 'short', year: 'numeric' },
                        'en-NG'
                      )}
                    </td>
                    <td className="px-4 py-3">₦{p.amountNgn.toLocaleString()}</td>
                    <td className="text-primary-600 px-4 py-3">
                      ₦{p.commissionNgn.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-green-700">
                      ₦{p.therapistAmountNgn.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={badge(p.status)}>{p.status}</span>
                      {p.flagReason && (
                        <p className="mt-0.5 text-xs text-red-500">{p.flagReason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.status === 'pending' && (
                        <button
                          onClick={() => approve(p.id)}
                          disabled={approving === p.id}
                          className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {approving === p.id ? '…' : 'Release'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
